import React, { useState, useEffect, useRef } from 'react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, Text, useTexture } from '@react-three/drei';
import { a, useSpring } from '@react-spring/three';
import * as THREE from 'three';
import './MenuScene.css';

// Import texture URL
import buildingTexture1Url from './textures/buildingTexture1.png';
// Import the InteractiveTouchDesigner and InteractiveMediaPipe components
import InteractiveTouchDesigner from './InteractiveTouchDesigner';
import InteractiveMediaPipe from './InteractiveMediaPipe'; // New MediaPipe component

function CameraController({ targetPosition, lookAtTarget }) {
  const { camera } = useThree();
  const [spring, api] = useSpring(() => ({
    position: camera.position.toArray(),
    config: { mass: 1, tension: 170, friction: 26 },
  }));

  useEffect(() => {
    api.start({ position: targetPosition });
  }, [targetPosition, api]);

  useFrame(() => {
    const [x, y, z] = spring.position.get();
    camera.position.set(x, y, z);
    camera.lookAt(...lookAtTarget);
  });

  return null;
}

function Terrain({ width = 100, height = 100, segments = 100 }) {
  const meshRef = useRef();
  useEffect(() => {
    const geometry = meshRef.current.geometry;
    const positionAttribute = geometry.attributes.position;
    const vertex = new THREE.Vector3();
    for (let i = 0; i < positionAttribute.count; i++) {
      vertex.fromBufferAttribute(positionAttribute, i);
      const elevation = Math.sin(vertex.x * 0.1) * 2 + Math.cos(vertex.y * 0.1) * 2;
      vertex.z = elevation;
      positionAttribute.setXYZ(i, vertex.x, vertex.y, vertex.z);
    }
    positionAttribute.needsUpdate = true;
    geometry.computeVertexNormals();
  }, []);
  return (
    <mesh ref={meshRef} receiveShadow rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[width, height, segments, segments]} />
      <meshStandardMaterial color="#556B2F" side={THREE.DoubleSide} />
    </mesh>
  );
}

function Building({ position, size, texture }) {
  return (
    <mesh position={position} castShadow>
      <boxGeometry args={size} />
      <meshStandardMaterial map={texture} />
    </mesh>
  );
}

function Buildings({ textureUrl }) {
  const texture = useTexture(textureUrl);
  return (
    <>
      <Building position={[-15, 0, 5]} size={[4, 4, 8]} texture={texture} />
      <Building position={[10, 0, -10]} size={[6, 6, 12]} texture={texture} />
      <Building position={[25, 0, 10]} size={[3, 3, 6]} texture={texture} />
      <Building position={[-20, 0, -15]} size={[5, 5, 10]} texture={texture} />
    </>
  );
}

export default function MenuScene() {
  // Extend rooms to include mediapipe
  const rooms = {
    projects: { cameraPos: [-20, 10, 20], center: [-20, 0, 0], label: 'Projects' },
    cv: { cameraPos: [0, 10, 20], center: [0, 0, 0], label: 'CV' },
    demos: { cameraPos: [20, 10, 20], center: [20, 0, 0], label: 'Demos' },
    touchdesigner: { cameraPos: [0, 10, 15], center: [0, 0, 0], label: 'TouchDesigner' },
    mediapipe: { cameraPos: [0, 10, 15], center: [0, 0, 0], label: 'Gesture Control' } // New room
  };

  const [currentRoom, setCurrentRoom] = useState('cv');

  // Conditional rendering for special rooms
  if (currentRoom === 'touchdesigner') {
    return (
      <div className="menu-container">
        <button
          style={{
            position: 'absolute',
            top: 20,
            left: 20,
            zIndex: 2,
            padding: '10px 20px',
            fontSize: '1rem',
            cursor: 'pointer'
          }}
          onClick={() => setCurrentRoom('cv')}
        >
          Back to Menu
        </button>
        <InteractiveTouchDesigner />
      </div>
    );
  }

  // Render MediaPipe room with a similar approach
  if (currentRoom === 'mediapipe') {
    return (
      <div className="menu-container">
        <button
          style={{
            position: 'absolute',
            top: 20,
            left: 20,
            zIndex: 2,
            padding: '10px 20px',
            fontSize: '1rem',
            cursor: 'pointer'
          }}
          onClick={() => setCurrentRoom('cv')}
        >
          Back to Menu
        </button>
        <InteractiveMediaPipe />
      </div>
    );
  }

  const { cameraPos, center } = rooms[currentRoom];

  return (
    <div className="menu-container">
      <div className="menu-overlay">
        <ul>
          {Object.entries(rooms).map(([key, room]) => (
            <li
              key={key}
              onClick={() => setCurrentRoom(key)}
              className={currentRoom === key ? 'active' : ''}
            >
              {room.label}
            </li>
          ))}
        </ul>
      </div>

      <Canvas shadows camera={{ position: cameraPos, fov: 50 }}>
        <ambientLight intensity={0.5} />
        <pointLight position={[50, 50, 50]} castShadow />
        <CameraController targetPosition={cameraPos} lookAtTarget={center} />
        <Terrain width={100} height={100} segments={100} />
        <Buildings textureUrl={buildingTexture1Url} />
        <Text position={[-20, 4, 0]} fontSize={2} color="white" anchorX="center" anchorY="middle">
          Projects Room
        </Text>
        <Text position={[0, 4, 0]} fontSize={2} color="white" anchorX="center" anchorY="middle">
          CV Room
        </Text>
        <Text position={[20, 6, 0]} fontSize={2} color="white" anchorX="center" anchorY="middle">
          Demos Room
        </Text>
        <OrbitControls enableZoom={false} enablePan={false} />
      </Canvas>
    </div>
  );
}
